import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import I18n from '@module/i18n';
import tcModel from '@module/consent-manager/tc-model';

import Chevron from './chevron';
import { initPurposeListState } from './helper';
import { DescriptionContainer, PurposeDescription } from './descriptions';

import Switch from '@component/form/element/switch';
import { ConnectedCustomPurposes } from './custom-purposes';

export function PurposeList({ gvlLanguage }) {
  const [preferences, setPreferences] = useState(
    initPurposeListState(gvlLanguage)
  );

  useEffect(() => {
    setPreferences(initPurposeListState(gvlLanguage));
  }, [gvlLanguage]);

  const handlePurposeConsent = (id, checked) => {
    const newPref = { ...preferences };
    newPref[id].purposeConsent = checked;
    setPreferences(newPref);

    if (checked) {
      tcModel.purposeConsents.set(id);
      return;
    }

    tcModel.purposeConsents.unset(id);
  };

  const handleLegitimateInterest = (id, checked) => {
    const newPref = { ...preferences };
    newPref[id].purposeLegitimateInterest = checked;
    setPreferences(newPref);

    if (checked) {
      tcModel.purposeLegitimateInterests.set(id);
      return;
    }

    tcModel.purposeLegitimateInterests.unset(id);
  };

  const createSwitchName = (name, id) => {
    return `${name}-${id}`;
  };

  const content = {
    title: {
      __html: I18n.t('transfer.consent-manager.preferences-headline'),
    },
    intro: {
      __html: I18n.t('transfer.consent-manager.preferences-text'),
    },
    allowCookies: {
      __html: I18n.t('transfer.consent-manager.preferences-text-allow-cookies'),
    },
    byWeTransfer: I18n.t('transfer.consent-manager.switch-by-we-transfer'),
    byPartners: I18n.t('transfer.consent-manager.switch-by-partners'),
  };

  const toggleDescription = (id) => {
    const newPref = { ...preferences };
    newPref[id].descriptionVisible = !newPref[id].descriptionVisible;
    setPreferences(newPref);
  };

  return (
    <div className="welcome__purpose-list">
      <div
        className="welcome__cookie-notice-title"
        dangerouslySetInnerHTML={content.title}
      />
      <div
        className="welcome__cookie-notice-text"
        dangerouslySetInnerHTML={content.intro}
      />
      <div
        className="welcome__cookie-notice-text"
        dangerouslySetInnerHTML={content.allowCookies}
      />
      {Object.values(preferences).map((consent, index) => (
        <div className="consent-accordion" key={index}>
          <div className="consent-accordion__section">
            <div
              className="consent-accordion__header"
              onClick={() => toggleDescription(consent.id)}
            >
              <Chevron
                className={classNames('consent-accordion__icon', {
                  'consent-accordion__icon--rotated':
                    consent.descriptionVisible,
                })}
                width="10"
              />
              <p className="consent-accordion__title">{consent.name}</p>
            </div>
            <div className="consent-accordion__switches">
              <Switch
                name={createSwitchName('purpose-legitimate-int', consent.id)}
                label={content.byWeTransfer}
                onChange={(checked) =>
                  handleLegitimateInterest(consent.id, checked)
                }
                checked={consent.purposeLegitimateInterest}
              />
              <Switch
                disabled={!consent.purposeConsentVisible}
                name={createSwitchName('purpose-consent', consent.id)}
                label={content.byPartners}
                onChange={(checked) =>
                  handlePurposeConsent(consent.id, checked)
                }
                checked={consent.purposeConsent}
              />
            </div>
          </div>
          <DescriptionContainer isVisible={consent.descriptionVisible}>
            <PurposeDescription description={consent.descriptionLegal} />
          </DescriptionContainer>
        </div>
      ))}
      <ConnectedCustomPurposes />
    </div>
  );
}

PurposeList.propTypes = {
  gvlLanguage: PropTypes.object,
};
